import { ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from '@/components/ui/empty';
import { AppLink } from './AppLink';
import { useAuthStore } from '../stores/authStore';
import { homeRouteForRole } from '../lib/constants/authRoutes';
import { FORBIDDEN_REASONS, type ForbiddenReason } from '../lib/constants/forbiddenReasons';

// Literal t() calls per case: check-i18n-keys only verifies static keys, and the `never` default
// makes a new reason a compile error instead of a silently wrong explanation.
function describeReason(reason: ForbiddenReason, t: TFunction): string {
  switch (reason) {
    case FORBIDDEN_REASONS.permission:
      return t('common.forbidden.descriptionPermission');
    case FORBIDDEN_REASONS.tenantAdminOnly:
      return t('common.forbidden.descriptionTenantAdminOnly');
    case FORBIDDEN_REASONS.superAdminOnly:
      return t('common.forbidden.descriptionSuperAdminOnly');
    default: {
      const unhandled: never = reason;
      return unhandled;
    }
  }
}

/**
 * Rendered by PermissionGuard / RoleGuard in place of the route's Outlet when the signed-in user
 * is not allowed on it. A rejection must be visible: a redirect to the dashboard leaves the user
 * guessing why the page they clicked never opened (AB#2138).
 */
export function ForbiddenState({ reason }: { reason: ForbiddenReason }) {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const description = describeReason(reason, t);

  return (
    <div className="flex flex-1 p-4 sm:p-6">
      <Empty role="alert">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <ShieldAlert aria-hidden="true" />
          </EmptyMedia>
          <h1 className="text-lg font-medium tracking-tight">{t('common.forbidden.title')}</h1>
          <EmptyDescription>{description}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button asChild>
            <AppLink to={homeRouteForRole(role)}>{t('common.forbidden.backToHome')}</AppLink>
          </Button>
        </EmptyContent>
      </Empty>
    </div>
  );
}

export default ForbiddenState;
